import { selectAuth } from '@brands/store/selectors/auth';
import { AuthState } from '@brands/store/slices/authSlice';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '../../hooks/useReduxHook';
import { UserRoleName } from '../../services/identity/types/UserProfile';
import Container from '@brands/Components/Container/Container';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import AuditTrail from './SuperAdmin/AuditTrail/AuditTrail';
import CasesList from './SuperAdmin/CasesList';
import ClientsList from './SuperAdmin/Clients/ClientsList';
import { AdminAddUserContent } from './SuperAdmin/AddUser/AddUserPage';
import UserSearch from './SuperAdmin/UserSearch';

import styles from './adminDashboard.module.scss';

const AdminDashboard: React.FC = () => {
  const user: AuthState = useAppSelector(selectAuth);
  const location = useLocation();
  const [addUserOverlayOpen, setAddUserOverlayOpen] = useState(false);
  const [addUserRoleQuery, setAddUserRoleQuery] = useState<string | null>(null);

  const requestInlineAddUser = useCallback((roleQuery: string | null) => {
    setAddUserRoleQuery(roleQuery);
    setAddUserOverlayOpen(true);
  }, []);

  const closeInlineAddUser = useCallback(() => {
    setAddUserOverlayOpen(false);
    setAddUserRoleQuery(null);
  }, []);

  useEffect(() => {
    if (addUserOverlayOpen && location.hash !== '#users') {
      closeInlineAddUser();
    }
  }, [addUserOverlayOpen, closeInlineAddUser, location.hash]);

  if (!user.userInfo) {
    window.location.replace('/');
    return null;
  }

  const isAdmin =
    user.userInfo.role.name === UserRoleName.Admin ||
    user.userInfo.role.name === UserRoleName.SuperAdmin ||
    user.userInfo.role.name === UserRoleName.OrganizationAdmin;

  if (!isAdmin) {
    return <NotFoundPage />;
  }

  const hash = location.hash;
  let content: JSX.Element;

  if (addUserOverlayOpen) {
    content = <AdminAddUserContent createRoleFromQuery={addUserRoleQuery} onClose={closeInlineAddUser} />;
  } else if (hash === '#users') {
    content = <UserSearch requestInlineAddUser={requestInlineAddUser} />;
  } else if (hash === '#clients') {
    // The list only. Creating and configuring a client — plan selection,
    // reviewer networks, access codes, service pricing — is the part that was
    // deferred, and none of it is backed by an endpoint yet.
    content = <ClientsList />;
  } else if (hash === '#audit-trail') {
    content = <AuditTrail />;
  } else {
    content = <CasesList />;
  }

  return (
    <Container
      patientDashboard
      isVisibleOnMobile={false}
      childClassName={styles.adminDashboardShell}
      containerStyle={{ overflowY: 'hidden' }}
      childrenStyle={{
        alignItems: 'flex-start',
        width: '100%',
      }}
    >
      <div className={styles.adminLayout}>
        <main className={styles.adminContent}>{content}</main>
      </div>
    </Container>
  );
};

export default AdminDashboard;
